import React, { useState, useMemo, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRestaurantContext } from '../../context/RestaurantContext';
import { useCartContext } from '../../context/CartContext';
import { useOrderContext } from '../../context/OrderContext';
import { useQueueContext } from '../../context/QueueContext';
import MenuItemCard from './components/MenuItemCard';
import CategoryTabs from './components/CategoryTabs';
import MenuItemModal from './components/MenuItemModal';
import VerticalCategorySidebar from './components/VerticalCategorySidebar';
import HighDensityListItem from './components/HighDensityListItem';
import HorizontalItemCard from './components/HorizontalItemCard';
import LinksSheet from './components/LinksSheet';
import MoneyUtils from '../../utils/moneyUtils';
import { getCategoryIconName } from '../../utils/categoryUtils';
import { PromotionBannerCarousel } from '../../components/PromotionComponents';
import { getPromotions, createCartHold, updateCartHold, checkCartHoldStatus, checkQueueAvailability } from '../../services/api/restApiAdapter';
import { useAuth } from '../../context/AuthContext';
import CartBottomSheet from './components/CartBottomSheet';
import JoinQueueSheet from './components/JoinQueueSheet';
import InfoSheet from './components/InfoSheet';
import { getActiveQueueEntry } from '../../services/api/restApiAdapter';
import BusinessHeader from './components/BusinessHeader';
import Icon from '../../components/AppIcon';
import QueueActionBanner from '../../components/ui/QueueActionBanner';
import StoreStatusSheet from './components/StoreStatusSheet';
import { computeStoreStatus } from '../../utils/storeStatusUtils';
import ItemRepeatSheet from './components/ItemRepeatSheet';
import ActiveOrdersSheet from './components/ActiveOrdersSheet';
import AnimatedCookingBadge from './components/AnimatedCookingBadge';


// ─── Biller Code Screen ────────────────────────────────────────────────────────
const BillerCodeScreen = ({ code, onDone, theme }) => {
  const accentColor = theme?.secondary || '#000';
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-50/95 backdrop-blur-sm p-6 pointer-events-auto">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm text-center space-y-5 animate-in fade-in zoom-in duration-200">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Thanks for ordering!</h2>
          <h3 className="text-lg font-bold text-gray-700">Your Order Code</h3>
        </div>
        <p className="text-sm text-gray-500">Show this code to the cashier to place your order.</p>

        <div
          className="flex gap-3 justify-center py-6 rounded-2xl"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          {code.split('').map((digit, i) => (
            <div
              key={i}
              className="w-14 h-16 flex items-center justify-center rounded-xl text-3xl font-extrabold shadow-sm"
              style={{ backgroundColor: accentColor, color: theme?.primary || '#fff' }}
            >
              {digit}
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400">Code is valid for this session only.</p>

        <button
          onClick={onDone}
          className="w-full py-3.5 rounded-xl text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all scale-wrapper active:scale-95 uppercase tracking-wider"
        >
          Done
        </button>
      </div>
    </div>
  );
};

// ─── Main Menu Page ───────────────────────────────────────────────────
const MenuPage = () => {
  const navigate = useNavigate();
  const { details, menuItems: contextMenuItems, theme, restaurantId, selfOrderConfig, tableNumber, capabilities } = useRestaurantContext();
  const {
    cartData,
    menuItemModal,
    openMenuItemModal,
    closeMenuItemModal,
    clearCart,
    isQueueMode,
    addItem,
    getItemsInCartById,
  } = useCartContext();
  const { ensureGuestAuth } = useAuth();
  const { orders } = useOrderContext();
  const { activeQueue, refreshQueue } = useQueueContext();

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [layoutMode, setLayoutMode] = useState('list');

  const isOrderingEnabled = capabilities?.isOrderingEnabled ?? true;

  // Dynamically force layoutMode to 'list' if self-ordering is completely disabled
  useEffect(() => {
    if (selfOrderConfig) {
      if (!isOrderingEnabled) {
        setLayoutMode('list');
      } else {
        setLayoutMode(selfOrderConfig.layoutType || 'card');
      }
    }
  }, [selfOrderConfig, isOrderingEnabled]);

  // Biller Code State
  const [billerCode, setBillerCode] = useState(null);
  const [activeCartHoldId, setActiveCartHoldId] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [showQueueInfo, setShowQueueInfo] = useState(false);
  const [queueInfoMessage, setQueueInfoMessage] = useState(null);
  const [isJoinQueueSheetOpen, setIsJoinQueueSheetOpen] = useState(false);
  const [isCartSheetOpen, setIsCartSheetOpen] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isBottomBarVisible, setIsBottomBarVisible] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [slideDirection, setSlideDirection] = useState(1);
  const [isStatusSheetOpen, setIsStatusSheetOpen] = useState(false);
  const [itemRepeatSheet, setItemRepeatSheet] = useState({ isOpen: false, item: null });
  const [editCartItem, setEditCartItem] = useState(null);
  const [isActiveOrdersSheetOpen, setIsActiveOrdersSheetOpen] = useState(false);
  const fullHeaderRef = useRef(null);
  const collapseThresholdRef = useRef(180); // measured on mount, default fallback
  const isManualScroll = useRef(false);

  // Measure the full header height once on mount so we collapse at the right pixel.
  // We use a ref (not state) so reading it in handleScroll never causes re-renders.
  useEffect(() => {
    if (fullHeaderRef.current) {
      collapseThresholdRef.current = fullHeaderRef.current.offsetHeight;
    }
  }, []);

  // Scroll handler — drives both header collapse and bottom bar visibility
  const handleScroll = (e) => {
    const scrollTop = e.currentTarget.scrollTop;

    // Header collapse: toggle exactly when the full header scrolls out of view.
    // Since the full header no longer changes height, there is no bouncing.
    if (scrollTop > collapseThresholdRef.current && !isHeaderCollapsed) {
      setIsHeaderCollapsed(true);
    } else if (scrollTop <= collapseThresholdRef.current && isHeaderCollapsed) {
      setIsHeaderCollapsed(false);
    }

    // Bottom bar: hide on scroll down, show on scroll up
    if (scrollTop > lastScrollTop) {
      setIsBottomBarVisible(false);
    } else if (scrollTop < lastScrollTop - 5) {
      setIsBottomBarVisible(true);
    }

    setLastScrollTop(scrollTop);

    // If reached the bottom of the page, highlight the last active category
    const isAtBottom = scrollTop + e.currentTarget.clientHeight >= e.currentTarget.scrollHeight - 15;
    if (isAtBottom && activeCategories.length > 0 && !isManualScroll.current) {
      setSelectedCategory(activeCategories[activeCategories.length - 1]);
    }
  };

  // ─── Show Code Generation ──────────────────────────────────────────────────
  const handleShowCodeAction = async () => {
    setIsGeneratingCode(true);
    try {
      await ensureGuestAuth();
      const cartItems = cartData.items.map((item) => {
        const cust = item.customizations || {};
        return {
          menuItemId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          portion: cust.portion || null,
          addons: cust.addons || [],
          isCombo: item.isCombo || false,
          comboComponents: cust.comboComponents || null,
          cookingInstruction: item.specialInstructions || null,
          category: item.category || null,
          taxRuleId: item.taxRuleId || null,
          hsnCode: item.hsnCode || null,
          // Legacy support (matches official cartItemSchema)
          portionSize: cust.portion?.name || null,
        };
      });

      const payload = {
        items: cartItems,
        tableNumber: tableNumber || null,
        customerNote: null,
      };

      if (activeCartHoldId) {
        const result = await updateCartHold(restaurantId, activeCartHoldId, payload);
        if (!result.success) throw new Error(result.error || 'Failed to update code');
        // Do not alter billerCode, it remains the same
      } else {
        const result = await createCartHold(restaurantId, payload);
        if (!result.success) throw new Error(result.error || 'Failed to generate code');
        setBillerCode(result.data.code);
        setActiveCartHoldId(result.data.cartId);
      }
    } catch (err) {
      console.error('showCode error:', err);
      // Wait, there is no toast context here, so fallback to alert
      alert(err.message || 'Could not process code request. Please try again.');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // ─── Cart Sheet Management ─────────────────────────────────────────────────
  const handleOpenCartSheet = async () => {
    setIsCartSheetOpen(true);

    // Silently update the cart hold on the server so the POS gets the latest items
    if (activeCartHoldId && billerCode) {
      try {
        const cartItems = cartData.items.map((item) => {
          const cust = item.customizations || {};
          return {
            menuItemId: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            portion: cust.portion || null,
            addons: cust.addons || [],
            isCombo: item.isCombo || false,
            comboComponents: cust.comboComponents || null,
            cookingInstruction: item.specialInstructions || null,
            category: item.category || null,
            taxRuleId: item.taxRuleId || null,
            hsnCode: item.hsnCode || null,
            portionSize: cust.portion?.name || null,
          };
        });

        const payload = {
          items: cartItems,
          tableNumber: tableNumber || null,
          customerNote: null,
        };

        await updateCartHold(restaurantId, activeCartHoldId, payload);
      } catch (err) {
        console.error('Failed background sync of cart hold:', err);
      }
    }
  };

  const handleClearCart = () => {
    setActiveCartHoldId(null);
    setBillerCode(null);
    clearCart();
  };

  const handleCartClose = async () => {
    if (activeCartHoldId) {
      try {
        const res = await checkCartHoldStatus(restaurantId, activeCartHoldId);
        if (res.success && (res.data.status === 'claimed' || res.data.status === 'expired')) {
          handleClearCart();
        }
      } catch (err) {
        console.error('Failed to check cart status on close:', err);
      }
    }
    setIsCartSheetOpen(false);
  };

  const handleJoinQueueClick = async () => {
    const isQueueConfigEnabled = details?.supportsQueue && selfOrderConfig?.queueManagement?.enabled;
    
    if (!isQueueConfigEnabled) {
      setQueueInfoMessage('The waitlist is currently disabled by the restaurant. Please contact staff for seating availability.');
      setShowQueueInfo(true);
      return;
    }

    // Perform an authoritative availability check (respects timing windows, auto-stop, etc.)
    try {
      const avail = await checkQueueAvailability(restaurantId);
      if (avail.success) {
        if (avail.available) {
          setIsJoinQueueSheetOpen(true);
        } else {
          setQueueInfoMessage(avail.reason || 'Waitlist is currently not accepting entries.');
          setShowQueueInfo(true);
        }
      } else {
        // Fallback to basic enabled check if API fails
        setIsJoinQueueSheetOpen(true);
      }
    } catch (err) {
      console.error('Availability check failed:', err);
      setIsJoinQueueSheetOpen(true);
    }
  };

  // Fetch promotions once when restaurantId is available
  useEffect(() => {
    if (!restaurantId) return;
    
    getPromotions(restaurantId).then((result) => {
      if (result.success) setPromotions(result.data || []);
    });
  }, [restaurantId]);

  const menuItems = contextMenuItems || [];

  // ── Compute live operational status ─────────────────────────────────────────
  const storeStatus = useMemo(() => computeStoreStatus(details), [details]);

  const isBeverageItem = (item) => {
    return /(drink|beverage|juice|coffee|tea|shake|smoothie|water)/i.test(item.category || item.name);
  };

  // Compute categories with dynamically resolved category images from the first item with an image
  const categoryImages = useMemo(() => {
    const images = {};
    menuItems.forEach((item) => {
      if (item.category && item.imageUrl && !images[item.category]) {
        images[item.category] = item.imageUrl;
      }
    });
    return images;
  }, [menuItems]);

  // Filter items by search query
  const filteredMenuItems = useMemo(() => {
    let result = menuItems;

    if (searchQuery) {
      result = result.filter((item) =>
        (item.name || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return result;
  }, [menuItems, searchQuery]);

  // Group by category
  const groupedMenuItems = useMemo(() => {
    return filteredMenuItems.reduce((acc, item) => {
      const cat = item.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [filteredMenuItems]);

  // Only categories that have items in the filtered list (to automatically skip/hide empty ones)
  const activeCategories = useMemo(() => {
    const cats = Array.from(
      new Set(filteredMenuItems.map((item) => item.category).filter(Boolean))
    );
    // Sort them in the original category definition order
    const originalCats = Array.from(
      new Set(menuItems.map((item) => item.category).filter(Boolean))
    );
    return originalCats.filter((cat) => cats.includes(cat));
  }, [filteredMenuItems, menuItems]);

  const cardCategories = useMemo(() => {
    return ['All', ...activeCategories];
  }, [activeCategories]);

  // Category scroll spy observer
  const categoryRefs = useRef({});

  // Popular items: rating > 3.5
  const popularItems = useMemo(
    () => menuItems.filter((item) => item.rating && item.rating > 3.5),
    [menuItems]
  );

  // Dynamic Scroll Spy Intersection Observer
  useEffect(() => {
    const scrollContainerId = 'menu-scroll-container';
    
    // Tiny timeout to ensure DOM element exists after layout mode toggle
    const timer = setTimeout(() => {
      const scrollRoot = document.getElementById(scrollContainerId);
      if (!scrollRoot) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (isManualScroll.current) return;
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const cat = entry.target.getAttribute('data-category');
              if (cat) {
                setSelectedCategory(cat);
              }
            }
          });
        },
        {
          root: scrollRoot,
          rootMargin: '-10% 0px -70% 0px',
          threshold: 0,
        }
      );

      const currentRefs = Object.values(categoryRefs.current);
      currentRefs.forEach((ref) => {
        if (ref) observer.observe(ref);
      });

      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timer);
  }, [groupedMenuItems, layoutMode]);

  // ─── Smart item click routing ──────────────────────────────────────────────
  const handleItemClick = (item) => {
    if (!isOrderingEnabled) {
      // View-only mode: open modal for information display
      openMenuItemModal(item);
      return;
    }

    const hasPortionsOrAddons =
      (item.portions && item.portions.length > 0) ||
      (item.addons && item.addons.length > 0) ||
      (item.addOns && item.addOns.length > 0) ||
      item.isCombo;

    if (!hasPortionsOrAddons) {
      // Simple item — add directly with no sheet
      addItem(item);
      return;
    }

    const existingEntries = getItemsInCartById(item.id || item._id);
    if (existingEntries.length > 0) {
      // Item already in cart with customizations — show repeat sheet
      setItemRepeatSheet({ isOpen: true, item });
    } else {
      // First add — open customization modal
      setEditCartItem(null);
      openMenuItemModal(item);
    }
  };

  const handleItemRepeatSheetClose = () => {
    setItemRepeatSheet({ isOpen: false, item: null });
  };

  const handleItemRepeatEdit = (cartItem) => {
    // Close repeat sheet and open modal in edit mode
    setItemRepeatSheet({ isOpen: false, item: null });
    setEditCartItem(cartItem);
    openMenuItemModal(itemRepeatSheet.item || cartItem);
  };

  const handleMenuItemModalClose = () => {
    setEditCartItem(null);
    closeMenuItemModal();
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    isManualScroll.current = true;
    setTimeout(() => {
      isManualScroll.current = false;
    }, 800);

    const scrollContainer = document.getElementById('menu-scroll-container');
    if (!scrollContainer) return;

    if (layoutMode === 'list') {
      const target = document.getElementById(`list-category-${category.replace(/\s+/g, '-')}`);
      if (target) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top + scrollContainer.scrollTop;
        scrollContainer.scrollTo({
          top: relativeTop - 80, // Offset to align category neatly below the compact sticky header
          behavior: 'smooth'
        });
      }
    } else {
      if (category === 'All') {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const target = document.getElementById(`category-${category.replace(/\s+/g, '-')}`);
        if (target) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const relativeTop = targetRect.top - containerRect.top + scrollContainer.scrollTop;
          scrollContainer.scrollTo({
            top: relativeTop - 80,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  const cartItemCount = cartData?.itemCount ?? 0;
  const cartTotal = cartData?.total ?? 0;

  // Only consider active in-progress orders within the last 24 hours for floating activity card
  const activeOrders = useMemo(() => {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return (orders || []).filter((o) => {
      const isStatusActive = !['completed', 'cancelled', 'failed', 'delivered'].includes(o.orderStatus?.toLowerCase());
      const orderTime = new Date(o.createdAt || o.created_at || 0).getTime();
      const isRecent = orderTime >= twentyFourHoursAgo;
      return isStatusActive && isRecent;
    });
  }, [orders]);

  // When an item is added to cart or cart count increases, automatically show the bottom bar
  const prevCartCountRef = useRef(cartItemCount);
  useEffect(() => {
    if (cartItemCount > prevCartCountRef.current) {
      setIsBottomBarVisible(true);
    }
    prevCartCountRef.current = cartItemCount;
  }, [cartItemCount]);

  // When a new active order is created/detected, also ensure bottom bar is visible
  const prevActiveOrdersCountRef = useRef(activeOrders.length);
  useEffect(() => {
    if (activeOrders.length > prevActiveOrdersCountRef.current) {
      setIsBottomBarVisible(true);
    }
    prevActiveOrdersCountRef.current = activeOrders.length;
  }, [activeOrders.length]);

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">

      {/* Main container */}
      <div 
        id="menu-scroll-container"
        onScroll={handleScroll}
        className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden scroll-smooth"
      >

        {/* Full header (banner + address info) — rendered in both modes and scrolls away naturally */}
        <div ref={fullHeaderRef}>
          <BusinessHeader 
            isCollapsed={false}
            restaurant={details} 
            theme={theme} 
            onSearch={(e) => {
              setSearchQuery(e.target.value);
              setSelectedCategory('All');
            }}
            searchQuery={searchQuery}
            selfOrderConfig={selfOrderConfig}
            onJoinQueue={handleJoinQueueClick}
            isQueueActive={details?.supportsQueue && selfOrderConfig?.queueManagement?.enabled}
            onViewStatus={() => navigate(`/restaurant/${restaurantId}/queue-status`)}
            layoutMode={layoutMode}
            onToggleLayout={() => setLayoutMode((m) => (m === 'card' ? 'list' : 'card'))}
            storeStatus={storeStatus}
            onStatusClick={() => setIsStatusSheetOpen(true)}
          />
        </div>

        {/* Sticky wrapper for header elements: handles compact bar and sticky filters dynamically */}
        <div className="sticky top-0 z-30 bg-white shadow-sm flex-shrink-0">
          
          {/* Compact header bar: transitions height based on scroll collapse */}
          <div
            style={{
              height: isHeaderCollapsed ? '64px' : '0px',
              overflow: 'hidden',
              transition: 'height 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
              backgroundColor: 'white',
            }}
          >
            <div className="h-[64px] flex items-center gap-3 px-4 border-b border-gray-100">
              {/* Small logo avatar — clickable, carries presence dot */}
              <button
                onClick={() => setIsStatusSheetOpen(true)}
                className="relative flex-shrink-0 active:scale-90 transition-transform"
              >
                {(details?.imageUrl || details?.image_url) ? (
                  <img
                    src={details?.imageUrl || details?.image_url}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover border border-gray-100"
                  />
                ) : (
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black"
                    style={{ backgroundColor: `${theme?.secondary || '#222'}18`, color: theme?.secondary || '#222' }}
                  >
                    {(details?.name || '').charAt(0).toUpperCase()}
                  </div>
                )}
                {/* Presence dot — bottom-right of avatar */}
                {storeStatus && (
                  <div
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                      storeStatus.isOpen ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                )}
              </button>

              {/* Name + address */}
              <div className="flex-1 min-w-0">
                <h1 className="text-base font-black text-gray-900 leading-tight truncate">
                  {details?.name || ''}
                </h1>
                {details?.address && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Icon name="MapPin" size={10} color="#9ca3af" />
                    <span className="text-[10px] text-gray-400 truncate leading-none">
                      {details.address}
                    </span>
                  </div>
                )}
              </div>

              {/* Search shortcut */}
              <button
                onClick={() => {
                  document.getElementById('menu-scroll-container')?.scrollTo({ top: 0, behavior: 'smooth' });
                  const searchInput = document.querySelector('input[placeholder="Search menu..."]');
                  if (searchInput) searchInput.focus();
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full transition-opacity active:opacity-60 flex-shrink-0 hover:bg-gray-50"
                style={{ color: theme?.secondary || '#222' }}
              >
                <Icon name="Search" size={18} />
              </button>

              {/* Layout Mode Toggle - Hidden when kiosk mode (ordering) is enabled */}
              {!isOrderingEnabled && (
                <button
                  onClick={() => {
                    setLayoutMode((m) => (m === 'card' ? 'list' : 'card'));
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full transition-opacity active:opacity-60 flex-shrink-0 hover:bg-gray-50"
                  style={{ color: theme?.secondary || '#222' }}
                >
                  <Icon name={layoutMode === 'card' ? 'List' : 'Grid'} size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Card or Grid layout horizontal CategoryTabs strip */}
          {(layoutMode === 'card' || layoutMode === 'grid') && (
            <div className="bg-white/80 border-b border-gray-100/50">
              <CategoryTabs
                categories={cardCategories}
                selectedCategory={selectedCategory}
                onSelectCategory={handleSelectCategory}
              />
            </div>
          )}
        </div>

        {/* Active Queue Status Bar (when joined) */}
        {activeQueue && (
          <div className="px-4 py-2 bg-white flex-shrink-0">
            <button
              onClick={() => navigate(`/restaurant/${restaurantId}/queue-status`)}
              className={`w-full h-12 rounded-2xl border flex items-center justify-between px-5 font-bold transition-all duration-300 ${
                activeQueue.status === 'called'
                  ? 'bg-green-50 border-green-100 text-green-700'
                  : 'bg-orange-50 border-orange-100 text-orange-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  activeQueue.status === 'called' ? 'bg-green-500 animate-pulse' : 'bg-orange-500'
                }`} />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest opacity-70 leading-none mb-1">
                    Queue Status
                  </span>
                  <span className="text-sm font-black tracking-tight leading-none">
                    Spot #{activeQueue.positionInQueue || '?'} <span className="opacity-40 mx-1">|</span> {activeQueue.queueCode || activeQueue.queueNumber}
                  </span>
                </div>
              </div>
              <div className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest bg-white px-3.5 py-2 rounded-xl shadow-sm ${
                activeQueue.status === 'called' ? 'text-green-600' : 'text-orange-600'
              }`}>
                View Status
                <Icon name="ChevronRight" size={14} />
              </div>
            </button>
          </div>
        )}

        {/* Main Content Layout Renderer */}
        {layoutMode === 'list' ? (
          /* Split View List Layout (Unified scroll container) */
          <div className="flex flex-row items-start w-full relative">
            {/* Left Vertical Sidebar (Stays sticky within page scroll) */}
            <VerticalCategorySidebar
              categories={activeCategories}
              selectedCategory={selectedCategory}
              onSelectCategory={handleSelectCategory}
              categoryImages={categoryImages}
              theme={theme}
              style={{
                position: 'sticky',
                top: isHeaderCollapsed ? '64px' : '0px',
                height: isHeaderCollapsed ? 'calc(100vh - 64px)' : 'calc(100vh - 240px)',
                transition: 'top 0.22s cubic-bezier(0.4, 0, 0.2, 1), height 0.22s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />

            {/* Right Pane (High Density items list scrolling with page) */}
            <div className="flex-1 px-4 pb-24 min-w-0">
              {filteredMenuItems.length > 0 ? (
                activeCategories.map((category) => {
                  const items = groupedMenuItems[category];
                  if (!items || items.length === 0) return null;
                  const catImg = categoryImages[category];
                  const defaultIcon = getCategoryIconName(category);

                  return (
                    <div
                      key={category}
                      id={`list-category-${category.replace(/\s+/g, '-')}`}
                      data-category={category}
                      ref={(el) => (categoryRefs.current[category] = el)}
                      className="scroll-mt-[80px] pt-3"
                    >
                      {/* Category Title Row */}
                      <div className="flex items-center gap-2 pb-2 pt-1">
                        <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                          {catImg ? (
                            <img src={catImg} alt={category} className="w-full h-full object-cover" />
                          ) : (
                            <Icon name={defaultIcon} size={12} color="#6b7280" />
                          )}
                        </div>
                        <h3 className="font-bold text-sm text-gray-900 capitalize">
                          {category}
                        </h3>
                        <div className="flex-1 h-[1px] bg-gray-300 ml-2" />
                      </div>

                      {/* Items */}
                      <div className="flex flex-col">
                        {items.map((item) => (
                          <HighDensityListItem
                            key={item.id || item._id}
                            item={item}
                            onItemClick={handleItemClick}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col justify-center items-center h-48">
                  <p className="text-gray-500 text-sm">
                    {searchQuery ? 'No items match your search.' : 'No menu items available.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Card layout render */
          <>
            {/* Promotion Carousel — only shown when viewing All categories */}
            {selectedCategory === 'All' && promotions.length > 0 && (
              <PromotionBannerCarousel promotions={promotions} theme={theme} />
            )}

            <div className="flex flex-col mt-1 relative pb-20">
              {filteredMenuItems.length > 0 ? (
                <>
                  {/* Popular Items strip */}
                  {selectedCategory === 'All' && popularItems.length > 0 && (
                    <div className="mt-2 ml-3">
                      <div className="h-5 flex items-center">
                        <p className="text-md font-bold">🔥 Popular Items</p>
                      </div>
                      <div className="flex overflow-x-auto max-w-screen space-x-4 my-2 [&::-webkit-scrollbar]:hidden shrink-0 pr-3">
                        {popularItems.map((item) => (
                          <HorizontalItemCard
                            key={item.id || item._id}
                            item={item}
                            onClick={() => handleItemClick(item)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Grouped menu items by category */}
                  {activeCategories.map((category) => {
                    const items = groupedMenuItems[category];
                    if (!items || items.length === 0) return null;
                    return (
                      <div 
                        key={category}
                        id={`category-${category.replace(/\s+/g, '-')}`}
                        data-category={category}
                        ref={(el) => (categoryRefs.current[category] = el)}
                        className="scroll-mt-[80px]"
                      >
                        <div className="h-5 flex items-center mx-3 mt-2">
                          <p className="text-md font-bold">
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </p>
                        </div>
                        {layoutMode === 'grid' ? (
                          <div className={`m-3 grid gap-3 ${selfOrderConfig?.gridColumns === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                            {items.map((item) => (
                              <MenuItemCard
                                key={item.id || item._id}
                                item={item}
                                onItemClick={handleItemClick}
                                onOpenCart={() => setIsCartSheetOpen(true)}
                                isGridView={true}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="m-3">
                            {items.map((item) => (
                              <MenuItemCard
                                key={item.id || item._id}
                                item={item}
                                onItemClick={handleItemClick}
                                onOpenCart={() => setIsCartSheetOpen(true)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Branded Footer */}
                  <div className="mt-28 pb-[30px] flex flex-col items-center justify-center space-y-3 px-6 text-center">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black tracking-[0.3em] uppercase text-gray-400">Powered by</span>
                        <a 
                          href="https://swadho.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm font-black text-gray-700 tracking-tight hover:opacity-70 transition-opacity"
                        >
                          Swadho.com
                        </a>
                      </div>
                      <p className="text-[11px] font-medium tracking-wide text-gray-400 italic">
                        "Great food deserves great tech"
                      </p>
                    </div>
                    
                    <div className="h-[1px] w-12 bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                    
                    <div className="flex items-center gap-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                      Made with <span className="text-red-500/70 animate-pulse">❤️</span> in Kerala
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center h-full min-h-50 mt-16">
                  <p className="text-center text-gray-500">
                    {searchQuery
                      ? 'No items match your search.'
                      : 'No menu items available.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

      </div>

      {/* Floating Cart & Orders Bar */}
      <AnimatePresence>
        {(cartItemCount > 0 || activeOrders.length > 0) && isBottomBarVisible && (
          <motion.div 
            className="fixed bottom-0 left-0 right-0 z-30 flex flex-col gap-2 p-3.5 pb-5 pointer-events-none"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
          >
            
            {/* Orders Summary Card */}
            {activeOrders.length > 0 && (
              <div 
                onClick={() => setIsActiveOrdersSheetOpen(true)}
                className="pointer-events-auto shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 bg-white/95 backdrop-blur-md overflow-hidden w-full max-w-lg mx-auto rounded-2xl flex items-center justify-between px-3.5 py-2.5 cursor-pointer active:scale-[0.99] transition-transform"
              >
                <div className="flex items-center gap-2.5">
                  <AnimatedCookingBadge 
                    count={activeOrders.length} 
                    themeColor={theme?.secondary || '#222'} 
                  />
                  <div className="flex flex-col">
                    <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest leading-none mb-1">Recent Activity</span>
                    <span className="text-xs font-black text-gray-900 leading-tight">Track Your Orders</span>
                  </div>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('orders');
                  }}
                  className="px-4 py-2 rounded-xl font-bold text-xs shadow-sm uppercase tracking-wider transition-all"
                  style={{ backgroundColor: theme?.secondary || '#222', color: theme?.primary || '#fff' }}
                >
                  View
                </motion.button>
              </div>
            )}

            {/* Cart Summary Card — Hidden in Queue Mode to avoid overlap */}
            {cartItemCount > 0 && !isQueueMode && capabilities?.isOrderingEnabled && (
              <div
                className="pointer-events-auto p-[1px] shadow-[0_15px_40px_rgba(0,0,0,0.06)] overflow-hidden cursor-pointer w-full max-w-lg mx-auto rounded-2xl"
                style={{ backgroundColor: `${theme?.secondary || '#222'}25` }}
                onClick={() => {
                  if (capabilities?.canShowCode && !capabilities?.canOrder && !capabilities?.canPayAndOrder) {
                    handleOpenCartSheet();
                  } else {
                    navigate('checkout');
                  }
                }}
              >
                <div className="bg-white/90 backdrop-blur-2xl flex items-center rounded-[15px] justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center border"
                        style={{ backgroundColor: `${theme?.secondary || '#222'}15`, borderColor: `${theme?.secondary || '#222'}20` }}
                      >
                        <span className="font-black text-lg leading-none" style={{ color: theme?.secondary || '#222' }}>{cartItemCount}</span>
                      </div>
                      <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.15em] leading-none mb-1">Cart Summary</span>
                      <span className="text-base font-black leading-tight mt-0.5" style={{ color: theme?.secondary || '#222' }}>
                        {cartTotal > 0 ? MoneyUtils.format(cartTotal) : 'Checkout Now'}
                      </span>
                    </div>
                  </div>
                  {capabilities?.canShowCode && !capabilities?.canOrder && !capabilities?.canPayAndOrder ? (
                    <div
                      className="px-4 py-2 rounded-xl font-bold text-[10px] shadow-md flex items-center justify-center gap-2 uppercase tracking-widest transition-all min-w-[100px]"
                      style={{ backgroundColor: theme?.secondary || '#222', color: theme?.primary || '#fff' }}
                    >
                      View Cart
                    </div>
                  ) : (
                    <div
                      className="px-4 py-2 rounded-xl font-bold text-[10px] shadow-md flex items-center gap-2 uppercase tracking-widest transition-all group min-w-[100px]"
                      style={{ backgroundColor: theme?.secondary || '#222', color: theme?.primary || '#fff' }}
                    >
                      Checkout
                      <svg className="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Queue Mode Action Banner — Integrated into floating stack */}
            {isQueueMode && cartItemCount > 0 && selfOrderConfig?.queueManagement?.allowPreOrder && (
              <QueueActionBanner
                itemCount={cartItemCount}
                total={cartTotal}
                theme={theme}
                isQueueMode={isQueueMode}
                isFixed={false}
                isCompact={false}
                onClick={() => {
                  if (capabilities?.canShowCode && !capabilities?.canOrder && !capabilities?.canPayAndOrder) {
                    handleOpenCartSheet();
                  } else {
                    navigate('checkout');
                  }
                }}
              />
            )}

          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Bottom Sheet Output — for ShowCode-only mode */}
      {capabilities?.canShowCode && !capabilities?.canOrder && !capabilities?.canPayAndOrder && !isQueueMode && (
        <CartBottomSheet
          isOpen={isCartSheetOpen}
          onClose={handleCartClose}
          onClearCart={handleClearCart}
          theme={theme}
          isGeneratingCode={isGeneratingCode}
          billerCode={billerCode}
          onGetCode={handleShowCodeAction}
          restaurantId={restaurantId}
          activeCartHoldId={activeCartHoldId}
        />
      )}

      {/* Biller Code Overlay (kept just in case, but typically handled by CartBottomSheet now) */}
      {!selfOrderConfig?.enabledModes?.includes('showCode') && billerCode && (
        <BillerCodeScreen
          code={billerCode}
          onDone={() => setBillerCode(null)}
          theme={theme}
        />
      )}

      {/* Menu Item Modal */}
      <MenuItemModal
        isOpen={menuItemModal?.isOpen}
        onClose={handleMenuItemModalClose}
        editCartItem={editCartItem}
      />

      {/* Item Repeat Sheet */}
      <ItemRepeatSheet
        isOpen={itemRepeatSheet.isOpen}
        item={itemRepeatSheet.item}
        onClose={handleItemRepeatSheetClose}
        onAddNew={(item) => {
          setEditCartItem(null);
          openMenuItemModal(item);
        }}
        onEdit={handleItemRepeatEdit}
      />
      <JoinQueueSheet
        isOpen={isJoinQueueSheetOpen}
        onClose={() => setIsJoinQueueSheetOpen(false)}
        theme={theme}
        onJoinSuccess={(data) => {
          refreshQueue();
          // Redirect to status page after successful join
          navigate(`/restaurant/${restaurantId}/queue-status`);
        }}
      />

      {/* Standalone Banner removed in favor of integrated floating stack above */}


      {/* Queue Info Sheet */}
      <InfoSheet 
        isOpen={showQueueInfo}
        onClose={() => setShowQueueInfo(false)}
        title="Waitlist Issue"
        message={queueInfoMessage || "The waitlist is currently disabled by the restaurant. Please contact staff for seating availability."}
        theme={theme}
      />

      {/* Store Status Sheet */}
      <StoreStatusSheet
        isOpen={isStatusSheetOpen}
        onClose={() => setIsStatusSheetOpen(false)}
        theme={theme}
        storeStatus={storeStatus}
      />

      {/* Active Orders Bottom Sheet */}
      <ActiveOrdersSheet
        isOpen={isActiveOrdersSheetOpen}
        onClose={() => setIsActiveOrdersSheetOpen(false)}
        orders={orders}
        onOpenOrdersScreen={() => navigate('orders')}
        theme={theme}
      />
    </div>
  );
};

export default MenuPage;
